import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  Button,
  Avatar,
  Stack,
  Typography,
  Chip,
  Box,
  Paper,
  CircularProgress,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { SwapHoriz, Person } from '@mui/icons-material';
import { Scrollbar } from 'src/components/scrollbar';
import type { EmployeeTransferDto, BranchDto } from 'src/api/employee-transfer.api';

// ----------------------------------------------------------------------

interface BranchEmployeeTableProps {
  employees: EmployeeTransferDto[];
  availableBranches: BranchDto[];
  individualTargetBranches: Record<string, string>;
  onIndividualTargetBranchChange: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onIndividualTransfer: (employeeId: string) => void;
  transferLoading: Record<string, boolean>;
  disabled?: boolean;
}

export function BranchEmployeeTable({
  employees,
  availableBranches,
  individualTargetBranches,
  onIndividualTargetBranchChange,
  onIndividualTransfer,
  transferLoading,
  disabled = false,
}: BranchEmployeeTableProps) {
  const handleTargetBranchChange = (employeeId: string, branchId: string) => {
    onIndividualTargetBranchChange((prev) => ({
      ...prev,
      [employeeId]: branchId,
    }));
  };

  const getEmployeeInitials = (firstName: string, lastName: string) => 
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  if (employees.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Person sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Çalışan bulunamadı
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Bu şubede transfer edilecek çalışan bulunmuyor
        </Typography>
      </Paper>
    );
  }

  return (
    <Scrollbar>
      <TableContainer component={Paper} sx={{ minWidth: 800 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Çalışan</TableCell>
              <TableCell>Mevcut Şube</TableCell>
              <TableCell>Hedef Şube</TableCell>
              <TableCell align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {employees.map((employee) => {
              const isTransferring = transferLoading[employee.id] || false;
              const targetBranch = individualTargetBranches[employee.id];
              
                             // Filter out employee's current branch from available branches
               // availableBranches already filtered to same company, just exclude current branch
               const filteredBranches = availableBranches.filter(
                 (branch) => branch.id !== employee.branchId
               );

              return (
                <TableRow
                  key={employee.id}
                  hover
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  {/* Employee Info */}
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {getEmployeeInitials(employee.firstName, employee.lastName)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">
                          {employee.firstName} {employee.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {employee.email}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  {/* Current Branch */}
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight="medium">
                        {employee.branchName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {employee.companyName}
                      </Typography>
                    </Stack>
                  </TableCell>

                  {/* Target Branch Selector */}
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select
                        value={targetBranch || ''}
                        onChange={(e) => handleTargetBranchChange(employee.id, e.target.value)}
                        disabled={disabled || isTransferring}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Hedef şube seçiniz</em>
                        </MenuItem>
                        {filteredBranches.map((branch) => (
                          <MenuItem key={branch.id} value={branch.id}>
                            <Stack spacing={0.5}>
                              <Typography variant="body2">
                                {branch.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {branch.companyName}
                              </Typography>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  {/* Transfer Action */}
                  <TableCell align="right">
                    <LoadingButton
                      variant="contained"
                      size="small"
                      startIcon={<SwapHoriz />}
                      onClick={() => onIndividualTransfer(employee.id)}
                      loading={isTransferring}
                      disabled={disabled || !targetBranch || isTransferring}
                      sx={{ minWidth: 120 }}
                    >
                      {isTransferring ? 'Transfer Ediliyor...' : 'Transfer Et'}
                    </LoadingButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Scrollbar>
  );
}
